import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Copy, Check, Download } from 'lucide-react';

export const Setup2FAPage = () => {
  const { setup2FA, confirm2FA, logout } = useAdminAuth();
  const [step, setStep] = useState('loading'); // loading, setup, verify
  const [setupData, setSetupData] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  useEffect(() => {
    const initSetup = async () => {
      try {
        const data = await setup2FA();
        setSetupData(data);
        setStep('setup');
      } catch (err) {
        setError(err.message);
        setStep('error');
      }
    };

    initSetup();
  }, [setup2FA]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await confirm2FA(totpCode);
      // Success - AdminAuthProvider will handle redirect
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    if (setupData?.recovery_codes) {
      navigator.clipboard.writeText(setupData.recovery_codes.join('\n'));
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 2000);
    }
  };

  const downloadRecoveryCodes = () => {
    if (setupData?.recovery_codes) {
      const content = `KEIKKA Admin Recovery Codes\n${'='.repeat(40)}\n\nStore these codes securely. Each code can only be used once.\n\n${setupData.recovery_codes.join('\n')}\n\nGenerated: ${new Date().toISOString()}`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'keikka-admin-recovery-codes.txt';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Setting up 2FA...</div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardContent className="pt-6">
            <Alert variant="destructive" className="bg-red-900/50 border-red-800">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button onClick={logout} className="w-full mt-4">
              Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">Set Up Two-Factor Authentication</CardTitle>
          <CardDescription className="text-gray-400">
            2FA is required for admin access. Use an authenticator app like Google Authenticator or Authy.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive" className="bg-red-900/50 border-red-800">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* QR Code */}
          <div className="text-center">
            <p className="text-sm text-gray-400 mb-3">Scan this QR code with your authenticator app:</p>
            <div className="bg-white p-4 rounded-lg inline-block">
              {setupData?.qr_code && (
                <img
                  src={`data:image/png;base64,${setupData.qr_code}`}
                  alt="2FA QR Code"
                  className="w-48 h-48"
                  data-testid="2fa-qr-code"
                />
              )}
            </div>
          </div>

          {/* Manual Entry */}
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">Or enter this code manually:</p>
            <code className="bg-gray-700 px-3 py-1 rounded text-green-400 text-sm">
              {setupData?.secret}
            </code>
          </div>

          {/* Recovery Codes */}
          <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
            <h3 className="text-yellow-400 font-semibold mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Recovery Codes - SAVE THESE NOW
            </h3>
            <p className="text-xs text-yellow-200/70 mb-3">
              These codes can be used to access your account if you lose your authenticator. Each code can only be used once.
            </p>
            <div className="bg-gray-900 rounded p-3 font-mono text-sm text-gray-300 grid grid-cols-2 gap-2">
              {setupData?.recovery_codes?.map((code, i) => (
                <div key={i}>{code}</div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={copyRecoveryCodes}
                className="flex-1 bg-transparent border-yellow-700 text-yellow-400 hover:bg-yellow-900/30"
              >
                {copiedCodes ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copiedCodes ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadRecoveryCodes}
                className="flex-1 bg-transparent border-yellow-700 text-yellow-400 hover:bg-yellow-900/30"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </Button>
            </div>
          </div>

          {/* Verification Form */}
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="totp" className="text-gray-300">
                Enter code from your authenticator app to confirm setup:
              </Label>
              <Input
                id="totp"
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                className="bg-gray-700 border-gray-600 text-white text-center text-2xl tracking-widest"
                placeholder="000000"
                autoComplete="one-time-code"
                data-testid="2fa-setup-code"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={loading || totpCode.length !== 6}
              data-testid="2fa-setup-confirm"
            >
              {loading ? 'Verifying...' : 'Confirm & Enable 2FA'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Setup2FAPage;
